import { Platform } from '@angular/cdk/platform';
import { Injectable } from '@angular/core';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { ModalService } from '@df/ui/modal/modal.service';
import find from 'lodash-es/find';
import { BehaviorSubject } from 'rxjs';
import { getWindow } from 'ssr-window';
import { PwaIntegrationService } from '../pwa-integration.service';
import { BeforeInstallPromptEvent, NPwa, UserChoice } from '../pwa.interface';
import { PWA_A2HS_MODAL_ID } from './pwa-a2hs.component.def';
import { TimeoutService } from '@df/ng/timeout.service';
import { App } from '@df/core/app';
import { SessionStorage } from '@df/browser/storage/decorator/session-storage';

@Injectable({
    providedIn: 'root'
})
export class PwaA2hsService {
    /** @public */
    preventPrompt: boolean = false;
    deferredPrompt = new BehaviorSubject<BeforeInstallPromptEvent | null>(null);
    modalPlatform: NPwa.EPlatform | undefined;

    /** @internal */
    @SessionStorage('pwa-a2hs-dismissed')
    dismissed?: boolean;

    isChrome?: boolean;

    constructor(
        protected app: App,
        protected modalService: ModalService,
        protected timeoutService: TimeoutService,
        protected platform: Platform,
        protected pwaIntegrationService: PwaIntegrationService,
        protected dfModalService: DfModalService
    ) {
        if (app.isPlatformBrowser) {
            this.isChrome = /CriOS/i.test(window.navigator?.userAgent);
        }
    }

    init(): void {
        this.loadModal();
    }

    loadModal(): void {
        if (!this.dismissed) {
            if (this.platform.ANDROID) {
                this.loadAndroid();
            }

            if (this.platform.IOS && this.platform.SAFARI && !this.isChrome && !this.pwaIntegrationService.isStandalone) {
                this.timeoutService.setTimeout(() => this.loadIos(), 300);
            }
        }
    }

    loadAndroid(): void {
        this.preventPrompt = true;

        const window = getWindow();

        window.addEventListener<any>('beforeinstallprompt', (event: BeforeInstallPromptEvent) => {
            if (this.preventPrompt) {
                // prevent Chrome 67 and earlier from automatically showing the prompt
                event.preventDefault();

                this.modalPlatform = NPwa.EPlatform.ANDROID;

                // stash the event so it can be triggered later.
                this.deferredPrompt.next(event);

                this.app.logger.info('a2hs installprompt');

                this.modalOpen();

                // wait for the user to respond to the prompt
                this.deferredPrompt.value!.userChoice.then((choiceResult: UserChoice) => {
                    if (choiceResult.outcome === 'accepted') {
                        this.app.logger.info('User accepted the A2HS prompt');
                    } else {
                        this.dismiss();
                        this.app.logger.info('User dismissed the A2HS prompt');
                    }

                    this.deferredPrompt.next(null);
                });
            }

            event.preventDefault();
        });
    }

    loadIos(): void {
        this.modalPlatform = NPwa.EPlatform.IOS;

        this.modalOpen();
    }

    dismiss(): void {
        this.dismissed = true;
    }

    modalOpen(): void {
        this.timeoutService.setTimeout(() => {
            this.dfModalService.open(undefined, PWA_A2HS_MODAL_ID);
        }, 300);
    }

    modalClose(): void {
        const modalRef = find(this.modalService?.openModals, modal => {
            return modal.componentInstance?.modalConfig?.id === PWA_A2HS_MODAL_ID;
        });

        modalRef?.close();

        this.dismiss();
    }
}
